import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import { useShallow } from 'zustand/react/shallow';

import { RetryInfoText } from '@common/components/RetryInfoText';
import { usePrevious } from '@modules/common/hooks';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useAppMetaStore } from '@/modules/floorplanV2/store/project/projectState';
import { LoadingPanel } from '@/modules/common/components/LoadingPanel';
import { IsLatestRevisionSelector } from '@/selectors';

import { useVersioningCallbacks } from '../hooks';
import { VersionCard } from './VersionCard';

import { useVersioningStore } from '../store';
import { logger } from '@common/logging/logger';

const boxSx = {
  display: 'flex',
  justifyContent: 'center',
  mx: 2,
  flex: '1 1 auto',
};

export const VersioningContainer = () => {
  const loadingStatus = useVersioningStore((state) => state.loadingStatus);
  const [isError, setIsError] = useState(false);
  const { t } = useTranslation();
  const { currentLoadedVersionNr, currentLoadedRevisionId } = useFloorplanDesignStore(
    useShallow((state) => ({
      currentLoadedVersionNr: state.metaData.version,
      currentLoadedRevisionId: state.metaData.revisionId,
    })),
  );
  const activeLayoutAlternative = useAppMetaStore((state) => state.activeLayoutAlternative);
  const versions = useVersioningStore((state) => state.versions);
  const loadedVersionNrPrevious = usePrevious(currentLoadedVersionNr);

  const { loadAll } = useVersioningCallbacks();

  const load = useCallback(
    (skipLoader = false) => {
      setIsError(false);

      return loadAll(skipLoader).catch(() => setIsError(true));
    },
    [loadAll],
  );

  const onRetry = useCallback(() => load(), [load]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load(Boolean(loadedVersionNrPrevious && currentLoadedVersionNr !== loadedVersionNrPrevious));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLoadedVersionNr]);

  useEffect(() => {
    // PLZ FIX ME
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (versions.length === 0) setIsError(true);
    else setIsError(false);
  }, [versions]);

  if (activeLayoutAlternative.status !== 'loaded') {
    logger.error('Expected activeLayoutAlternativeName to be defined');
    return null;
  }

  return (
    <Stack
      direction='column'
      maxHeight='400px'
      width='350px'
      sx={{
        overflowY: 'scroll',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      }}
    >
      {loadingStatus === 'loading' ? (
        <LoadingPanel />
      ) : (
        <>
          {isError && (
            <Stack sx={boxSx}>
              <RetryInfoText header={t('errors:versioning.list.fetch')} onRetry={onRetry} />
            </Stack>
          )}

          {!isError &&
            versions.map((version) => (
              <VersionCard
                key={version.id}
                id={version.id}
                versionNr={version.version}
                updatedAt={version.updatedAt}
                latest={IsLatestRevisionSelector.logic(
                  activeLayoutAlternative.data.lastRevisionId,
                  version.id,
                )}
                current={version.id === currentLoadedRevisionId}
              />
            ))}
        </>
      )}
    </Stack>
  );
};
